/**
 * <aa-media-progress> — segmented progress-bar pagination for a slideshow-component.
 *
 * Markup comes from snippets/aa-media-progress.liquid. The visible segments mirror the
 * slideshow's current slide; the transparent range input on top lets keyboard, touch
 * and screen reader users move between slides. Swipes are announced through a polite
 * live region (the input announces its own value while it has focus).
 */
class AAMediaProgress extends HTMLElement {
  /** @type {HTMLElement & { select?: Function, current?: number } | null} */
  #slideshow = null;

  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {HTMLElement | null} */
  #status = null;

  connectedCallback() {
    this.#slideshow = this.closest('slideshow-component');
    this.#input = this.querySelector('input[type="range"]');
    this.#status = this.querySelector('[data-status]');
    if (!this.#slideshow || !this.#input) return;

    this.#slideshow.addEventListener('slideshow:select', this.#handleSelect);
    this.#input.addEventListener('input', this.#handleInput);
    this.#render(this.#slideshow.current ?? 0);
  }

  disconnectedCallback() {
    this.#slideshow?.removeEventListener('slideshow:select', this.#handleSelect);
    this.#input?.removeEventListener('input', this.#handleInput);
  }

  get #count() {
    return Number(this.#input?.max) || 1;
  }

  /** @param {number} index */
  #statusText(index) {
    return (this.dataset.statusTemplate || '').replace('[index]', `${index + 1}`);
  }

  /** @param {number} index */
  #render(index) {
    if (!this.#input) return;
    const clamped = Math.min(Math.max(index, 0), this.#count - 1);
    for (const [i, segment] of this.querySelectorAll('.aa-media-progress__segment').entries()) {
      segment.classList.toggle('aa-media-progress__segment--active', i === clamped);
    }
    this.#input.value = `${clamped + 1}`;
    this.#input.setAttribute('aria-valuetext', this.#statusText(clamped));
  }

  /** @param {Event} event */
  #handleSelect = (event) => {
    // Ignore selects bubbling up from a nested slideshow.
    if (event.target !== this.#slideshow) return;
    const { index } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (typeof index !== 'number') return;

    this.#render(index);

    // A focused range input already announces its new value.
    if (document.activeElement === this.#input || !this.#status) return;
    const message = this.#statusText(Math.min(Math.max(index, 0), this.#count - 1));
    this.#status.textContent = '';
    requestAnimationFrame(() => {
      if (this.#status) this.#status.textContent = message;
    });
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    if (!this.#input || typeof this.#slideshow?.select !== 'function') return;
    const index = Number(this.#input.value) - 1;
    this.#slideshow.select(index, event);
    this.#render(index);
  };
}

if (!customElements.get('aa-media-progress')) {
  customElements.define('aa-media-progress', AAMediaProgress);
}
